import axiosInstance from 'src/utils/axios';
import parseQueryParams from '../../utils/query';
import { nanoid } from 'nanoid';
import { datadogRum } from '@datadog/browser-rum';
import {
  CUSTOM_DATA_KEY,
  parseJson,
} from '../ProntoHousingAdministration/QuestionMapping/utils';
import { QUESTIONS_TYPES_WITH_CHOICES } from '../CertificationWorkbench/utils';
import { getRecordResponseKey } from 'src/pages/tenant/QuestionnaireV4/utils';
import { AnswerGroup } from './models';

const QUESTION_FIELDS = [
  'qit_id',
  'survey',
  'member',
  'order',
  'answers',
  'qit.id',
  'qit.order',
  'qit.calculation',
  'qit.necessity',
  'qit.question.id',
  'qit.question.name',
  'qit.question_choices',
  'qit.question.help_text',
  'qit.question.question_type',
  'qit.question.question_type_display',
];

const QUESTION_EXPAND = [
  'answers',
  'answers.calculation_result',
  'answers.question_choices',
  'qit.question',
  'qit.question_choices',
  'qit.calculation',
];

/**
 * Questions of a survey (one item per applicable QIT, answers included).
 * Thor: `GET questionnaire/survey/{survey}/questions/`.
 */
export async function getSurveyQuestions(surveyId, queryParams = {}) {
  const { data } = await axiosInstance.get(
    `questionnaire/survey/${surveyId}/questions/?${parseQueryParams({
      expand: QUESTION_EXPAND,
      fields: QUESTION_FIELDS,
      ...queryParams,
    })}`
  );

  return data;
}

export async function getChildSchema(parentQitId, surveyId) {
  const data = await getSurveyQuestions(surveyId, {
    qit__parent_qit: parentQitId,
  });

  return data
    .filter((question) => question?.qit?.question?.question_type !== 'file')
    .sort((a, b) => a.qit.order - b.qit.order);
}

export async function getGroupSchema(questionGroupId, surveyId) {
  const data = await getSurveyQuestions(surveyId, {
    visible_only: true,
    question_group: questionGroupId,
    not_parent_qit: true,
    exclude_file_childrens: true,
    exclude_calculations: true,
  });

  return data
    .filter((question) => question?.qit?.question?.question_type !== 'file')
    .sort((a, b) => a.qit.order - b.qit.order);
}

export const objToFormData = (obj) => {
  const formData = new FormData();

  for (const key in obj) {
    if (Boolean(obj[key]) || typeof obj[key] === 'number') {
      formData.append(key, obj[key]);
    }
  }

  return formData;
};

export const readFile = (file) => {
  return new Promise((resolve, reject) => {
    // create a file reader to read the File Object
    const fr = new FileReader();

    // file reader onload event
    fr.onload = () => {
      resolve(fr.result);
    };

    // file reader error event
    fr.onerror = reject;

    // read file as data url
    fr.readAsDataURL(file);
  });
};

export const readImageDimensions = (imageSrc) => {
  return new Promise((resolve, reject) => {
    const img = new Image(); // create and empty image
    const imgDimensions = { width: 0, height: 0 }; // initialize image dimensions

    img.onload = () => {
      imgDimensions.width = img.width;
      imgDimensions.height = img.height;
      resolve(imgDimensions);
    };

    img.onerror = reject;

    img.src = imageSrc;
  });
};

export const imageToPDFDoc = async (file, instance, isBase64 = false) => {
  const { PDFNet } = instance.Core;
  await PDFNet.initialize();

  // create and empty DOCUMENT
  const doc = await PDFNet.PDFDoc.create();

  await PDFNet.runWithCleanup(async () => {
    // lock the document before a write operation
    // runWithCleanup will auto unlock when complete
    doc.lock();

    // ElementBuilder is used to build new Element objects
    const builder = await PDFNet.ElementBuilder.create();

    // ElementWriter is used to write Elements to the page
    const writer = await PDFNet.ElementWriter.create();
    const readerFile = isBase64 ? file : await readFile(file);
    const imgDimensions = await readImageDimensions(readerFile);

    // Start a new page with the image dimensions
    const pageRect = await PDFNet.Rect.init(
      0,
      0,
      imgDimensions.width,
      imgDimensions.height
    );
    const page = await doc.pageCreate(pageRect);

    // begin writing to the page
    writer.beginOnPage(page);

    // Create an Image that can be reused multiple times in the document or multiple on the same page.
    const pdfNetImage = await PDFNet.Image.createFromURL(doc, readerFile);
    const element = await builder.createImageFromMatrix(
      pdfNetImage,
      await PDFNet.Matrix2D.create(
        imgDimensions.width,
        0,
        0,
        imgDimensions.height,
        0,
        0
      )
    );

    writer.writePlacedElement(element);
    writer.end();

    // Add the new page to the document sequence
    doc.pagePushBack(page);
  });

  return doc;
};

export const getPdfFileObject = async (
  doc,
  annotManager,
  fileName = 'file.pdf',
  filterProntoAnnots = false
) => {
  let xfdfString = await annotManager.exportAnnotations();

  if (filterProntoAnnots) {
    const annotList = annotManager
      .getAnnotationsList()
      .filter(
        (annot) =>
          !parseJson(annot.getCustomData(CUSTOM_DATA_KEY))?.isProntoAnnotation
      );
    xfdfString = await annotManager.exportAnnotations({ annotList });
  }

  // extracting the pages instead use getFileData we avoid rendering issues with the annotations
  // looks like this method doesn't affect the viewer live PDF
  const pages = [...Array(doc?.getPageCount()).keys()].map((n) => n + 1); // all document pages [1, 2, 3...]
  const data = await doc.extractPages(pages, xfdfString);

  // const data = await doc.getFileData({ xfdfString });
  return new File([new Uint8Array(data)], `${fileName}.pdf`, {
    type: 'application/pdf',
    lastModified: new Date().getTime(),
  });
};

export const isAValidIndex = (i) => i >= 0;

export const isAStoredElement = (element) => {
  // the element status is not new and has valid id
  return !element?.isNew && typeof element?.id === 'number';
};

export const enumFileAnswerPagesOnMove = (answer) => {
  const newFileAnswers = [...(answer?.files || [])];
  let pageOrder = 0;

  for (let i = 0; i < newFileAnswers.length; i++) {
    const fileAnswer = { ...newFileAnswers[i], order: i };

    if (!fileAnswer.wasDeleted && isAStoredElement(fileAnswer)) {
      fileAnswer.hasChanges = true;
    }

    for (let i = 0; i < fileAnswer.pages.length; i++) {
      const page = fileAnswer.pages[i];
      if (page.wasDeleted) {
        continue;
      }

      page.number = pageOrder + 1;
      page.order = pageOrder;
      pageOrder += 1;

      if (isAStoredElement(page)) {
        page.hasChanges = true;
      }
    }

    newFileAnswers[i] = fileAnswer;
  }

  return newFileAnswers;
};

const markFileAnswerPagesAsDeleted = (answer, removedPages = []) => {
  const newFileAnswers = [...(answer?.files || [])];
  const deletedAnswers = [];

  for (let i = 0; i < newFileAnswers.length; i++) {
    const fileAnswer = { ...newFileAnswers[i] };
    // remove all the new pages deleted
    fileAnswer.pages = fileAnswer.pages.filter(
      (p) => !(removedPages.includes(p?.number) && p?.isNew)
    );

    // iterate over the pages to remove or mark as deleted the items
    for (let x = 0; x < fileAnswer.pages.length; x++) {
      const page = fileAnswer.pages[x];
      if (!removedPages.includes(page.number)) {
        continue;
      }

      // mark as deleted the page
      if (isAStoredElement(page)) {
        page.wasDeleted = true;
        page.number = null;
        page.order = null;
      }
    }

    const shouldDeleteFileAnswer =
      isAStoredElement(fileAnswer) &&
      !fileAnswer?.pages?.filter((page) => !page?.wasDeleted).length;
    if (shouldDeleteFileAnswer && !fileAnswer.wasDeleted) {
      fileAnswer.wasDeleted = shouldDeleteFileAnswer;
      fileAnswer.hasChanges = false;

      const newAnswer = buildFileAnswer({
        id: `${
          answer?.answer_group ? `${answer.answer_group}__` : ''
        }file-${i}__${nanoid()}`,
        isNew: true,
        order: fileAnswer.order,
      });

      deletedAnswers.push({ ...fileAnswer });
      newFileAnswers[i] = newAnswer;
    } else {
      newFileAnswers[i] = fileAnswer;
    }
  }

  return enumFileAnswerPagesOnMove({
    ...answer,
    files: [...newFileAnswers, ...deletedAnswers],
  });
};

export const removePagesEvent = (answer, removedPages = []) => {
  if (!answer?.files?.length) {
    return [];
  }
  return markFileAnswerPagesAsDeleted(answer, removedPages);
};

export const movePagesEvent = (answer) => {
  if (!answer?.files?.length) {
    return [];
  }
  return enumFileAnswerPagesOnMove(answer);
};

/**
 * contentChanged is a numbers Array with the pages that have changed
 */
export const onContentPageChange = (answer, contentChanged = []) => {
  const newFileAnswers = [...(answer?.files || [])];

  for (let i = 0; i < contentChanged.length; i++) {
    const pageNumberWithChanges = contentChanged[i];
    // look's for the file answer that contains the page with changes
    const fileAnswerIndex = newFileAnswers.findIndex((fileAnswer) =>
      fileAnswer.pages.some((page) => page.number === pageNumberWithChanges)
    );

    if (isAValidIndex(fileAnswerIndex)) {
      const fileAnswer = newFileAnswers[fileAnswerIndex];

      // looks for the file answer page with changes
      const pageIndex = fileAnswer.pages.findIndex(
        (page) => page.number === pageNumberWithChanges
      );
      if (isAValidIndex(pageIndex)) {
        // set as edited the page and the file answer if are existing elements on the DB
        fileAnswer.pages[pageIndex].hasChanges = isAStoredElement(
          fileAnswer.pages[pageIndex]
        );
        fileAnswer.hasChanges = isAStoredElement(fileAnswer);
      }
    }
  }

  return newFileAnswers;
};

export const addPagesEvent = (answer) => {
  const finalAnswer = { ...answer };

  for (let i = 0; i < finalAnswer.files.length; i++) {
    const fileAnswer = finalAnswer.files[i];

    // if the answer was changed to wasDeleted previously but new pages are inserted
    // that flag needs to be removed in order to update the answer correctly
    if (
      fileAnswer?.wasDeleted &&
      fileAnswer?.pages?.filter((p) => !p.wasDeleted)?.length
    ) {
      fileAnswer.wasDeleted = false;
    }
  }

  return enumFileAnswerPagesOnMove(finalAnswer);
};

export const buildFileAnswer = ({
  id,
  order,
  file_date,
  name,
  pages,
  pdf_file_url,
  status,
  isNew,
  hasChanges,
  wasDeleted,
}) => ({
  id: id || nanoid(),
  order: order || 0,
  file_date: file_date || null,
  name: name || null,
  pages: pages || [],
  pdf_file_url: pdf_file_url || '',
  status: status || 3,
  isNew: isNew || true,
  hasChanges: hasChanges || false,
  wasDeleted: wasDeleted || false,
});

export const extractPDFPages = async ({
  annotManager,
  doc,
  key = '',
  name = '',
  pagesToExtract = [],
  prefix = '',
  index = 0,
}) => {
  if (!annotManager || !doc || !pagesToExtract.length) {
    return {};
  }

  // only include annotations on the pages to extract
  const annotList = annotManager
    .getAnnotationsList()
    .filter((annot) => pagesToExtract.indexOf(annot.PageNumber) > -1);
  const xfdfString = await annotManager.exportAnnotations({ annotList });
  const data = await doc.extractPages(pagesToExtract, xfdfString);
  const file = new File(
    [new Uint8Array(data)],
    `${prefix}__${name}-${index}.pdf`,
    { type: 'application/pdf', lastModified: new Date().getTime() }
  );

  return { [key]: file };
};

export const STATUS_CODES = {
  temporal: 0,
  accepted: 1,
  rejected: 2,
  unanswered: 3,
  answered: 4,
  corrupted: 5,
};

// consider previously rejected
export const getPageStatus = (page) => {
  if ([STATUS_CODES.accepted].includes(page?.status)) {
    return page.status;
  }

  return STATUS_CODES.answered;
};

export const getFileAnswerStatus = (fileAnswer) => {
  if ([STATUS_CODES.accepted].includes(fileAnswer?.status)) {
    return fileAnswer.status;
  }

  const validPages = fileAnswer?.pages?.filter((p) => !p.wasDeleted)?.length;

  if (!validPages) {
    return STATUS_CODES.unanswered;
  }

  return STATUS_CODES.answered;
};

export const getAnswerStatus = (answer) => {
  if (STATUS_CODES.accepted === answer?.status) {
    return answer.status;
  }

  const pages = answer?.files?.reduce(
    (acc, a) => acc.concat(a?.pages?.filter((p) => !p?.wasDeleted) || []),
    []
  );

  if (!pages.length) {
    return STATUS_CODES.unanswered;
  }

  return STATUS_CODES.answered;
};

/**
 * URL of a survey answer top-level row (ordered files payload).
 * Thor: `questionnaire/survey_answer_ordered_survey_files/?survey=&qit=&answer_group=`.
 * `answer_group` is omitted when absent: the endpoint's FK filter rejects
 * `null`/`undefined` with a 400.
 */
export const getOrderedFilesUrl = ({ survey, qit, answer_group }) =>
  `questionnaire/survey_answer_ordered_survey_files/?${parseQueryParams({
    survey,
    qit,
    ...(answer_group === null ||
    answer_group === undefined ||
    answer_group === ''
      ? {}
      : { answer_group }),
    fields: ANSWER_FIELDS,
    expand: 'files',
  })}`;

export async function patchFileAnswersStatus(fileAnswerIds, status, answer) {
  for (const fileID of fileAnswerIds) {
    const body = { compliance_status: status };
    await axiosInstance.patch(
      `questionnaire/survey_file/${fileID}/update_compliance_status/`,
      body
    );
  }

  const { data: recordAnswers } = await axiosInstance.get(
    getOrderedFilesUrl({
      survey: answer.survey,
      qit: answer.qit,
      answer_group: answer.answer_group,
    })
  );
  return recordAnswers?.[0];
}

export const ANSWER_FIELDS = [
  'id',
  'answer_group',
  'name',
  'file_date',
  'status',
  'survey',
  'qit',
  'order',
  'files.id',
  'pdf_file_url',
  'pdf_file',
  'files.status',
  'files.name',
  'files.file_date',
  'files.pdf_file_url',
  'files.order',
  'files.status',
  'files.id',
  'survey_file_date',
  'files.pages.survey_file',
].join(',');

export const SIDEBAR_STYLES = {
  height: { xs: 'auto', md: '90vh' },
  width: '100%',
  maxWidth: { xs: '100%', md: '500px' },
  overflow: { xs: 'initial', md: 'auto' },
  display: 'flex',
  flexDirection: 'column',
};

export async function buildInitialDataForQuestionnaireDM({
  answerGroupID,
  surveyId,
  qitId,
  incomingPayload = {},
  answerGroupIndex,
}) {
  const answerGroup = new AnswerGroup();
  answerGroup.position = answerGroupIndex ?? null;

  if (typeof answerGroup?.position === 'number') {
    answerGroup.position += 1;
  }

  // get the top-level answer of this question
  const { data: questionAnswers } = await axiosInstance.get(
    getOrderedFilesUrl({
      survey: surveyId,
      qit: qitId,
      answer_group: answerGroupID,
    })
  );

  const topLevelAnswer = questionAnswers?.[0] || {};
  if (Object.keys(topLevelAnswer).length) {
    answerGroupID = topLevelAnswer?.answer_group;
  }

  return {
    ...incomingPayload,
    answerGroup,
    topLevelAnswer,
    answerGroupID,
  };
}

const buildQuestionSchema = (questions = []) => {
  const result = {};
  for (const question of questions) {
    result[question.qit_id] = question;
  }

  return result;
};

const getAnswerValue = (initialSchema = [], answer) => {
  if (!answer) {
    return null;
  }

  const question = initialSchema.find(
    (item) => item?.qit_id === answer?.qit_id
  );
  if (!question?.answers) {
    return null;
  }

  const answerValue = question.answers.find(
    (questionAnswer) => questionAnswer?.parent_file === answer?.parent_file
  );
  if (!answerValue) {
    return null;
  }

  return answerValue;
};

export const buildAnswerParentFile = (
  answerParentFile,
  initialSchema = [],
  answerGroup
) => {
  if (!answerParentFile || !answerGroup?.id) {
    return null;
  }

  const newParentFile = {
    id: answerParentFile.id,
    order: answerParentFile.order,
    answer_group: answerGroup.id,
    childAnswers: initialSchema.map((question) => {
      const exisingAnswer = getAnswerValue(initialSchema, {
        qit_id: question?.qit_id,
        parent_file: answerParentFile?.id,
      });

      return {
        id: exisingAnswer?.id || nanoid(),
        survey: question.survey,
        qit_id: question.qit_id,
        value: exisingAnswer?.[getRecordResponseKey(question)] || '',
        parent_file: answerParentFile?.id,
        calculation_result: exisingAnswer?.calculation_result,
      };
    }),
    errors: null,
  };

  return newParentFile;
};

export const buildParentFileAnswers = (
  answerGroup,
  initialSchema,
  buildSchema = true
) => {
  const result = {};

  if (buildSchema) {
    result.childSchema = buildQuestionSchema(initialSchema);
  }
  result.parentFileAnswers = {};

  if (!answerGroup?.answer?.files?.length) {
    return result;
  }

  for (const fileAnswer of answerGroup.answer.files) {
    // if (typeof fileAnswer?.id !== 'number') {
    //   continue;
    // }
    result.parentFileAnswers[fileAnswer.id] = buildAnswerParentFile(
      fileAnswer,
      initialSchema,
      answerGroup
    );
  }

  return result;
};

export const getQuestionAnswerValue = (answerGroup, question) => {
  if (!answerGroup) {
    return null;
  }

  if (!question?.answers) {
    return null;
  }

  const answerValue = question.answers.find(
    (questionAnswer) => questionAnswer?.answer_group === answerGroup
  );
  if (!answerValue) {
    return null;
  }

  return answerValue;
};

export const getGroupAnswers = (answerGroupID, groupSchema) => {
  const result = [];

  for (const question of groupSchema) {
    let exisingAnswer = null;

    if (typeof answerGroupID === 'number') {
      exisingAnswer = getQuestionAnswerValue(answerGroupID, question);
    }

    result.push({
      id: exisingAnswer ? exisingAnswer?.id || nanoid() : nanoid(),
      survey: question.survey,
      qit_id: question.qit_id,
      value: exisingAnswer
        ? exisingAnswer[getRecordResponseKey(question)] || ''
        : '',
      answer_group: answerGroupID,
    });
  }

  return result;
};

export const buildGroupAnswers = (
  answerGroupID,
  initialSchema,
  buildSchema = true
) => {
  const result = {};
  if (buildSchema) {
    result.groupSchema = buildQuestionSchema(initialSchema);
  }
  result.groupAnswers = getGroupAnswers(answerGroupID, initialSchema);
  return result;
};

const mapChildAnswer = (answer, childSchema) => {
  const answerKey = getRecordResponseKey(childSchema[answer?.qit_id]);
  let value = answer?.value;

  if (
    QUESTIONS_TYPES_WITH_CHOICES.includes(
      childSchema[answer?.qit_id]?.qit?.question?.question_type
    )
  ) {
    value = answer?.value?.map((v) => v?.id) || [];
  }
  if (
    childSchema[answer?.qit_id]?.qit?.question?.question_type === 'currency'
  ) {
    value = answer?.value.replace(/[^\d.-]/g, '');
  }

  return {
    survey_answer_id: typeof answer?.id === 'number' ? answer.id : null,
    parent_file: answer?.parent_file,
    survey: answer?.survey,
    qit_id: answer?.qit_id,
    [answerKey]: value,
  };
};

export const buildChildAnswersPayload = ({
  parentFileAnswers = {},
  childSchema,
}) => {
  // first we get all the child answers list
  const allChildAnswers = parentFileAnswers.flatMap((file) => {
    return file.children_answers?.filter((answer) => answer?.dirty);
  });
  return allChildAnswers.map((answer) => mapChildAnswer(answer, childSchema));
};

export const buildGroupAnswersPayload = ({
  finalGroupAnswers = [],
  groupSchema = {},
}) => {
  /* Filtering the answers to only those that have a value and then mapping them to a new object. */
  const answers = finalGroupAnswers
    .filter(
      (answer) =>
        answer?.dirty &&
        (typeof answer?.id === 'number' ||
          Boolean(answer.currentValue ?? answer.value))
    )
    .map((answer) => {
      // Use currentValue if available (from form edits), otherwise use value (from initial load)
      const valueToUse = answer.currentValue ?? answer.value;
      return {
        survey_answer_id: typeof answer?.id === 'number' ? answer.id : null,
        survey: answer?.survey,
        qit_id: answer?.qit_id,
        [getRecordResponseKey(groupSchema[answer?.qit_id])]:
          QUESTIONS_TYPES_WITH_CHOICES.includes(
            groupSchema[answer?.qit_id]?.qit?.question?.question_type
          )
            ? valueToUse?.map((v) => v?.id) || []
            : groupSchema[answer?.qit_id]?.qit?.question?.question_type ===
              'currency'
            ? valueToUse.replace(/[^\d.-]/g, '')
            : valueToUse,
      };
    });

  return answers;
};

export function rebuildAnswersStatus({ answerGroup, shouldRecalculateStatus }) {
  const finalAnswerGroup = JSON.parse(JSON.stringify(answerGroup));
  if (
    finalAnswerGroup?.answer?.status === STATUS_CODES.accepted ||
    !shouldRecalculateStatus
  ) {
    return finalAnswerGroup;
  }

  finalAnswerGroup.answer.status = getAnswerStatus(finalAnswerGroup.answer);

  for (let x = 0; x < finalAnswerGroup.answer.files.length; x++) {
    const fileAnswer = finalAnswerGroup.answer.files[x];
    fileAnswer.status = getFileAnswerStatus(fileAnswer);

    for (let y = 0; y < fileAnswer.pages.length; y++) {
      const page = fileAnswer.pages[y];
      page.status = getPageStatus(page);
    }
  }

  return finalAnswerGroup;
}

export function emitDatadogEvent(payload = {}) {
  // send event to data dog to track document manager changes
  const emitChanges = Object.keys(payload || {}).some((key) =>
    ['answerGroup'].includes(key)
  );
  if (
    import.meta.env.VITE_DATADOG_ID &&
    import.meta.env.VITE_DATADOG_TOKEN &&
    emitChanges
  ) {
    datadogRum.addAction(
      `DocManagerEvent: ${payload?.eventFrom || ''}`,
      payload
    );
  }
}

export const mergeDocuments = async (pdfInstance, filesArray) => {
  const { Core } = pdfInstance;
  const promises = [];
  let pageCounter = 1;
  const error = [];
  for (const file of filesArray) {
    try {
      const fileURL = file.pdf_file_url || file.file_url;
      const doc = await safelyCreatePDFtronDocument(Core, fileURL, 3);

      const pageCount = doc.getPageCount();
      file.pages = Array.from({ length: pageCount }, () => {
        const page = {
          number: pageCounter,
          surveyFileId: file.id,
        };
        pageCounter += 1;
        return page;
      });
      promises.push({ PdfFile: doc, id: file.id });
    } catch (err) {
      file.status = STATUS_CODES.corrupted;
      const corruptFileIndex =
        filesArray.findIndex((f) => f.id === file.id) + 1;
      error.push(`Answer ${corruptFileIndex}`);
    }
  }
  return Promise.all(promises)
    .catch((error) => {
      return { error: error?.serverResponse?.url || 'Unknown error' };
    })
    .then(async (files) => {
      const pdfFile = await generatePdfFile(Core, files);
      return {
        pdfFile,
        filesArray,
        error,
      };
    });
};

export const generatePdfFile = async (Core, files) => {
  if (files.error) {
    return files;
  }
  if (files.length < 1) {
    return null;
  }
  const { PDFNet } = Core;
  await PDFNet.initialize();
  const firstDocument = await files[0].PdfFile.getPDFDoc();
  const copyPages = [];
  for (
    const itr = await firstDocument.getPageIterator();
    await itr.hasNext();
    await itr.next()
  ) {
    copyPages.push(await itr.current());
  }
  const temporalDocument = await PDFNet.PDFDoc.create();
  for (let i = 0; i < copyPages.length; ++i) {
    await temporalDocument.pagePushBack(copyPages[i]);
  }
  const baseDoc = await Core.createDocument(temporalDocument);
  for (let i = 1; i < files.length; i++) {
    const newDoc = files[i].PdfFile;
    const newDocPageCount = newDoc.getPageCount();
    const pages = Array.from({ length: newDocPageCount }, (v, k) => k + 1);
    const pageIndexToInsert = baseDoc.getPageCount() + 1;
    await baseDoc.insertPages(newDoc, pages, pageIndexToInsert);
  }
  return baseDoc;
};

export async function movePages({
  answerFiles,
  pagesTreeSelection,
  targetIndex,
}) {
  const finalAnswerFiles = JSON.parse(
    JSON.stringify(answerFiles.filter((file) => !file?.wasDeleted))
  );
  const deletedFileAnswers = [];
  const movedPages = [];

  /* Filtering the pages of the file answer. */
  for (let i = 0; i < finalAnswerFiles.length; i++) {
    const fileAnswer = finalAnswerFiles[i];

    fileAnswer.pages = fileAnswer.pages.filter((p) => {
      const wasMoved = pagesTreeSelection.includes(p.number);
      // if is a moved page, is pushed to the moved pages array
      if (wasMoved) {
        movedPages.push(p);
      }
      return !wasMoved;
    });

    // if the file answer doesn't have pages is removed
    if (isAStoredElement(fileAnswer) && !fileAnswer?.pages?.length) {
      fileAnswer.wasDeleted = true;

      const newAnswer = buildFileAnswer({
        id: `${
          fileAnswer?.answer_group ? `${fileAnswer.answer_group}__` : ''
        }file-${i}__${nanoid()}`,
        isNew: true,
        order: fileAnswer.order,
      });

      deletedFileAnswers.push({ ...fileAnswer });
      finalAnswerFiles[i] = newAnswer;
    }
  }

  finalAnswerFiles[targetIndex].pages = [
    ...finalAnswerFiles[targetIndex].pages,
    ...movedPages.map((p) => {
      const markAsNew = deletedFileAnswers.some(
        (file) => file?.id === p?.survey_file
      );
      const result = {
        ...p,
        survey_file: finalAnswerFiles[targetIndex].id,
      };

      if (markAsNew) {
        result.isNew = true;
        result.pdf_file_url = '';
        result.id = null;
        result.survey_file = finalAnswerFiles[targetIndex].id;
      }

      return result;
    }),
  ];

  return [...finalAnswerFiles, ...deletedFileAnswers];
}

export function getSurveyAnswerMemberFilterKey() {
  return 'survey__cert_member';
}

export function getSurveyMemberFilterKey() {
  return 'cert_member';
}

export function filterLegacyDocManagerMembers(members = []) {
  return (members ?? []).filter((member) => {
    const memberTypeName = (
      member?.member_type?.name ||
      member?.member_type_name ||
      ''
    ).toLowerCase();
    return memberTypeName !== 'non-household member emergency contact';
  });
}

export function resolveDocManagerMemberOptions(
  certification,
  legacyMembers = []
) {
  if (certification) {
    return buildDocManagerMemberOptions(certification);
  }
  return filterLegacyDocManagerMembers(legacyMembers);
}

export function buildDocManagerMemberOptions(certification) {
  return (certification?.cert_household?.household_members ?? [])
    .filter(
      (m) =>
        (m?.member_type_name || '').toLowerCase() !==
        'non-household member emergency contact'
    )
    .map((m) => ({
      id: m.cert_member_id,
      cert_member_id: m.cert_member_id,
      first_name: m.first_name,
      last_name: m.last_name,
      email: m.primary_email,
      member_type: { name: m.member_type_name || '' },
      user: m.user_id ? { email: m.primary_email } : undefined,
    }));
}

export async function getCertificationMembers(id) {
  const params = {
    fields: [
      'cert_household.household_members.cert_member_id',
      'cert_household.household_members.first_name',
      'cert_household.household_members.last_name',
      'cert_household.household_members.primary_email',
      'cert_household.household_members.member_type_name',
      'cert_household.household_members.user_id',
    ],
    expand: ['cert_household.household_members'],
  };
  try {
    const res = await axiosInstance.get(
      `certification/certification/${id}/?${parseQueryParams(params)}`
    );
    return buildDocManagerMemberOptions(res?.data);
  } catch (error) {
    console.error(error);
  }
}

export const countFilePages = async (pdfInstance, files = []) => {
  let pageCounter = 1;
  for (const file of files) {
    try {
      const doc = await safelyCreatePDFtronDocument(
        pdfInstance.Core,
        file.pdf_file_url
      );
      const pageCount = doc.getPageCount();
      file.pages = Array.from({ length: pageCount }, () => {
        const page = {
          number: pageCounter,
          surveyFileId: file.id,
        };
        pageCounter += 1;
        return page;
      });
    } catch (err) {
      file.status = STATUS_CODES.corrupted;
    }
  }
  return files;
};

// Server answers (SurveyAnswerSerializer) carry `qit`; locally built rows carry `qit_id`.
export const getAnswerQitId = (answer) => answer?.qit_id ?? answer?.qit;

export const initialValuesBuilder = (childAnswers = []) => {
  const initialValues = {};
  for (const answer of childAnswers) {
    const qitId = getAnswerQitId(answer);
    initialValues[qitId] = '';
    if (answer.text) {
      initialValues[qitId] = answer.text;
    } else if (answer.question_choices.length) {
      initialValues[qitId] = answer.question_choices.map((id) => ({
        id,
      }));
    }
  }
  return initialValues;
};

export const mapSurveyAnswers = (answers = []) => {
  const surveyAnswers = {};
  for (const answer of answers) {
    surveyAnswers[getAnswerQitId(answer)] = answer;
  }
  return surveyAnswers;
};

export const areObjectListsEqualByKey = (list1 = [], list2 = [], key) => {
  // Extract values of the specified key from each list
  const values1 = list1.map((item) => item[key]).sort();
  const values2 = list2.map((item) => item[key]).sort();

  // Check if both lists have the same elements
  if (values1.length !== values2.length) {
    return false;
  }

  for (let i = 0; i < values1.length; i++) {
    if (values1[i] !== values2[i]) {
      return false;
    }
  }

  return true;
};

export const updateTopLevelAnswer = async (pdfInstance, topLevelAnswer) => {
  await pdfInstance.closeDocument();
  const { pdfFile, filesArray, error } = await mergeDocuments(
    pdfInstance,
    topLevelAnswer.files
  );
  if (error.length) {
    pdfInstance.showErrorMessage(
      `Something went wrong loading the following files:\n ${error.join('\n')}`
    );
  }

  topLevelAnswer.files = filesArray;

  await pdfInstance.loadDocument(pdfFile, {
    filename: `document.pdf`,
  });

  return {
    topLevelAnswer,
    error,
  };
};

export const safelyFetchDocument = async (fileURL, maxRetries) => {
  let attempt = 1;
  while (attempt <= maxRetries) {
    try {
      const response = await fetch(fileURL, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch file: ${response.statusText}. Attempt: ${attempt} of ${maxRetries} `
        );
      }
      return await response.blob();
    } catch (error) {
      console.error(error);
      if (attempt === maxRetries) {
        throw error;
      }
      attempt += 1;
    }
  }
};

export const safelyCreatePDFtronDocument = async (
  Core,
  fileURL,
  maxRetries = 3
) => {
  try {
    const blob = await safelyFetchDocument(fileURL, maxRetries);
    return await Core.createDocument(blob, {
      extension: 'pdf',
    });
  } catch {
    throw new Error('Failed to load document after multiple attempts.');
  }
};

export const safelyLoadPDFtronDocument = async ({
  pdfInstance,
  fileURL,
  maxRetries = 3,
  filename = 'document.pdf',
  errorMessage = 'Failed to load document after multiple attempts.',
}) => {
  try {
    const blob = await safelyFetchDocument(fileURL, maxRetries);
    return await pdfInstance.loadDocument(blob, {
      filename: filename,
      extension: 'pdf',
    });
  } catch {
    pdfInstance.showErrorMessage(errorMessage);
    throw new Error(errorMessage);
  }
};
